import React, { useState, useRef, useEffect } from "react";
import { string, node, oneOfType, arrayOf, bool, func } from "prop-types";
import ReactModal from "react-modal";
import { findByType } from "../util/findByType";
import { KeyCode } from "../util/keyCodes";

const Close = () => null;
Close.displayName = "ModalClose";
const Content = () => null;
Content.displayName = "ModalContent";
const Trigger = () => null;
Trigger.displayName = "ModalTrigger";

const Modal = ({ children, className, isOpen, contentLabel, overlayClassName, onClose }) => {
	const [showModal, setShowModal] = useState(isOpen);
	const isControlled = useRef(false);

	useEffect(() => {
		setShowModal(isOpen);
	}, [isOpen]);

	const handleCloseModal = () => {
		if (isControlled.current) setShowModal(false);
		if (onClose) onClose();
	};

	const HandleShowModal = () => {
		setShowModal(true);
	};

	const renderClose = () => {
		const close = findByType(children, Close);
		if (!close) {
			return null;
		}

		return (
			<button
				type="button"
				className={close.props.className}
				style={close.props.style}
				title="Close"
				aria-label="Close"
				onClick={handleCloseModal}>
				{close.props.children}
			</button>
		);
	};

	const renderContent = () => {
		const content = findByType(children, Content);
		if (!content) {
			return null;
		}
		return (
			<div className={content.props.className} style={content.props.style}>
				{content.props.children}
			</div>
		);
	};

	const renderTrigger = () => {
		const trigger = findByType(children, Trigger);
		if (!trigger) {
			return null;
		}
		isControlled.current = true;
		return (
			<div
				role="button"
				className={trigger.props.className}
				style={trigger.props.style}
				onClick={HandleShowModal}
				tabIndex="0"
				onKeyDown={e => {
					if (e.keyCode === KeyCode.CODE_ENTER) {
						HandleShowModal();
					}
				}}>
				{trigger.props.children}
			</div>
		);
	};
	return (
		<>
			{renderTrigger()}
			<ReactModal
				isOpen={showModal}
				contentLabel={contentLabel}
				onRequestClose={handleCloseModal}
				className={className}
				overlayClassName={overlayClassName}
				ariaHideApp={false}>
				{renderClose()}
				{renderContent()}
			</ReactModal>
		</>
	);
};
Modal.Close = Close;
Modal.Content = Content;
Modal.Trigger = Trigger;
Modal.propTypes = {
	children: oneOfType([arrayOf(node), node]).isRequired,
	className: string,
	/**
	 * To control modal state externally
	 */
	isOpen: bool,
	/**
	 * Accessibility label
	 */
	contentLabel: string.isRequired,
	overlayClassName: string,
	onClose: func,
};
Modal.defaultProps = {
	className: "",
	isOpen: false,
	overlayClassName: "",
	onClose: null,
};

export default Modal;
export { Modal };
